import { ChevronDown, ChevronRight, GitBranch, MessageSquare, Trash2 } from 'lucide-react'
import { BottomSheet } from '@/components/BottomSheet'
import { resolveConversationTitle } from '@/lib/conversations'
import type { ConversationTreeRow, MiniappConversation } from '@/pages/chat/chat-types'

type ConversationHistorySheetProps = {
  open: boolean
  rows: Array<ConversationTreeRow<MiniappConversation>>
  conversationId?: string
  onClose: () => void
  onSwitchConversation: (conversation: MiniappConversation) => void
  onToggleBranchCollapse: (conversationId: string) => void
  getTreePrefix: (row: ConversationTreeRow<MiniappConversation>) => string
  onBranchConversation: (conversation: MiniappConversation) => void
  onDeleteConversation: (conversation: MiniappConversation) => void
  getSpaceLabel: (spaceId?: string | null) => string
}

export function ConversationHistorySheet({
  open,
  rows,
  conversationId,
  onClose,
  onSwitchConversation,
  onToggleBranchCollapse,
  getTreePrefix,
  onBranchConversation,
  onDeleteConversation,
  getSpaceLabel,
}: ConversationHistorySheetProps) {
  return (
    <BottomSheet
      open={open}
      onClose={onClose}
      title="История диалогов"
      description="Список диалогов по всем проектам."
      maxBodyHeight="74vh"
    >
      <div style={{ padding: '2px 0 4px' }}>
        {!rows.length && (
          <p className="text-xs" style={{ padding: '12px 0', color: 'var(--dropdown-hint-color)' }}>
            Нет диалогов
          </p>
        )}
        {rows.map((row) => {
          const conv = row.conversation
          const displayTitle = resolveConversationTitle(conv.title, conv.last_message, 'Новый диалог')
          const dateStr = conv.updated_at
            ? new Date(conv.updated_at).toLocaleDateString('ru-RU', { day: 'numeric', month: 'short' })
            : ''
          const projectLabel = getSpaceLabel(conv.space_id)
          return (
            <div
              key={conv.id}
              className={`flex items-center rounded-xl transition-all ${conv.id === conversationId ? 'bg-telegram-button/10' : 'bg-transparent'}`}
              style={{ padding: '8px 10px', marginBottom: '4px', gap: '8px' }}
            >
              <button
                onClick={() => onSwitchConversation(conv)}
                className="flex-1 min-w-0 text-left flex items-center active:scale-[0.98]"
                style={{ gap: '8px' }}
              >
                <div className="flex items-center gap-1 shrink-0">
                  {row.depth > 0 && (
                    <span className="font-mono text-[10px] leading-none text-telegram-hint select-none">
                      {getTreePrefix(row)}
                    </span>
                  )}
                  {row.hasChildren ? (
                    <span
                      role="button"
                      tabIndex={0}
                      onClick={(event) => {
                        event.preventDefault()
                        event.stopPropagation()
                        onToggleBranchCollapse(conv.id)
                      }}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault()
                          event.stopPropagation()
                          onToggleBranchCollapse(conv.id)
                        }
                      }}
                      className="rounded-md glass-icon flex items-center justify-center active:scale-95 transition-transform"
                      style={{ width: '18px', height: '18px' }}
                      title={row.isCollapsed ? 'Развернуть ветку' : 'Свернуть ветку'}
                    >
                      {row.isCollapsed ? (
                        <ChevronRight size={11} className="text-telegram-hint" />
                      ) : (
                        <ChevronDown size={11} className="text-telegram-hint" />
                      )}
                    </span>
                  ) : (
                    <span style={{ width: '18px' }} />
                  )}
                  {row.depth > 0 ? (
                    <GitBranch size={13} className={conv.id === conversationId ? 'text-telegram-button' : 'text-telegram-hint'} />
                  ) : (
                    <MessageSquare size={14} className={conv.id === conversationId ? 'text-telegram-button' : 'text-telegram-hint'} />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{displayTitle}</p>
                  <div className="flex items-center gap-2 mt-0.5">
                    {dateStr && <span className="text-[9px] text-telegram-hint">{dateStr}</span>}
                    <span className="text-[9px] text-telegram-hint">{Number(conv.message_count || 0)} сообщ.</span>
                    <span className="text-[9px] text-telegram-hint truncate max-w-[120px]">{projectLabel}</span>
                    {Array.isArray(conv.selected_doc_ids) && conv.selected_doc_ids.length > 0 && (
                      <span className="text-[9px] text-telegram-button">{conv.selected_doc_ids.length} док.</span>
                    )}
                  </div>
                </div>
              </button>
              <button
                onClick={(event) => {
                  event.stopPropagation()
                  onBranchConversation(conv)
                }}
                className="shrink-0 rounded-lg flex items-center justify-center active:scale-90 transition-transform text-telegram-hint hover:text-telegram-button"
                style={{ width: '28px', height: '28px' }}
                title="Создать ветку"
              >
                <GitBranch size={13} />
              </button>
              <button
                onClick={(event) => {
                  event.stopPropagation()
                  onDeleteConversation(conv)
                }}
                className="shrink-0 rounded-lg flex items-center justify-center active:scale-90 transition-transform text-telegram-hint hover:text-red-500"
                style={{ width: '28px', height: '28px' }}
              >
                <Trash2 size={13} />
              </button>
            </div>
          )
        })}
      </div>
    </BottomSheet>
  )
}
